import type {
  ApproveInventoryAdjustment,
  CreateInventoryAdjustment,
  CreateInventoryCategory,
  CreateInventoryCategoryTypeOption,
  CreateInventoryItem,
  CreateInventoryLocation,
  CreateInventoryMovement,
  CreateInventoryMovementReasonOption,
  ExportOptions,
  ImportOptions,
  ImportPreviewRecord,
  ImportResult,
  InventoryAdjustment,
  InventoryAdjustmentFilters,
  InventoryAdjustmentReason,
  InventoryAdjustmentStatus,
  InventoryAllocationOptions,
  InventoryAllocationQuery,
  InventoryCategory,
  InventoryCategoryType,
  InventoryCategoryTypeOption,
  InventoryItem,
  InventoryItemImage,
  InventoryLocation,
  InventoryLocationType,
  InventoryLowStockItem,
  InventoryMovement,
  InventoryMovementHistoryResult,
  InventoryMovementReason,
  InventoryMovementReasonOption,
  InventoryMovementType,
  InventorySortField,
  InventoryStatus,
  InventoryStockHealthResult,
  InventoryTopMovingItem,
  InventoryValueSummaryResult,
  PaginationParams,
  RejectInventoryAdjustment,
  SortOrder,
  UpdateInventoryCategory,
  UpdateInventoryCategoryTypeOption,
  UpdateInventoryItem,
  UpdateInventoryLocation,
  UpdateInventoryMovementReasonOption,
} from '@scrambl/shared'
import type {
  StockCommand,
  StockCountPayload,
  StockMovementPayload,
} from '@scrambl/shared/offline'

import { apiClient } from './api-client'
import { CircuitBreaker } from './circuit-breaker'
import { createSingletonApi } from './singleton-factory'

// Custom pagination response type for inventory APIs
interface InventoryPaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

interface GetInventoryItemsParams extends PaginationParams {
  search?: string
  status?: InventoryStatus
  categoryId?: string
  locationId?: string
  lowStock?: boolean
  sortBy?: InventorySortField
  sortOrder?: SortOrder
  columnFilters?: Record<string, unknown>
}

interface GetMovementsParams extends PaginationParams {
  itemId?: string
  type?: InventoryMovementType
  reason?: InventoryMovementReason
}

interface GetCategoriesParams {
  type?: InventoryCategoryType
}

interface GetLocationsParams {
  type?: InventoryLocationType
}

interface GetMovementHistoryParams {
  days?: number
}

class InventoryApi {
  private circuitBreaker = new CircuitBreaker()

  private async makeRequest<T>(requestFn: () => Promise<T>): Promise<T> {
    if (!this.circuitBreaker.canMakeRequest()) {
      throw new Error('Service temporarily unavailable')
    }

    try {
      const result = await requestFn()
      this.circuitBreaker.recordSuccess()
      return result
    } catch (error) {
      this.circuitBreaker.recordFailure()
      throw error
    }
  }

  // =====================
  // Inventory Items
  // =====================

  async getInventoryItems(
    params: GetInventoryItemsParams = {},
  ): Promise<InventoryPaginatedResponse<InventoryItem>> {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (key === 'columnFilters' && typeof value === 'object') {
          queryParams.append(key, JSON.stringify(value))
        } else {
          queryParams.append(key, String(value))
        }
      }
    })

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : ''
    return this.makeRequest(() =>
      apiClient<InventoryPaginatedResponse<InventoryItem>>(
        `/inventory/items${queryString}`,
      ),
    )
  }

  async getInventoryItem(id: string): Promise<InventoryItem> {
    return this.makeRequest(() =>
      apiClient<InventoryItem>(`/inventory/items/${id}`),
    )
  }

  async createInventoryItem(data: CreateInventoryItem): Promise<InventoryItem> {
    return this.makeRequest(() =>
      apiClient<InventoryItem>('/inventory/items', {
        method: 'POST',
        body: data,
      }),
    )
  }

  async updateInventoryItem(
    id: string,
    data: UpdateInventoryItem,
  ): Promise<InventoryItem> {
    return this.makeRequest(() =>
      apiClient<InventoryItem>(`/inventory/items/${id}`, {
        method: 'PUT',
        body: data,
      }),
    )
  }

  async deleteInventoryItem(id: string): Promise<void> {
    return this.makeRequest(() =>
      apiClient<void>(`/inventory/items/${id}`, {
        method: 'DELETE',
      }),
    )
  }

  // =====================
  // Item images
  //
  // Upload uses the shared multipart endpoint:
  //   POST /files/upload/INVENTORY/:itemId  (handled by file.api.ts)
  // The methods below cover list/reorder/delete on top of the existing
  // Document store, so item images live alongside other documents instead
  // of needing their own pipeline.
  // =====================

  async getItemImages(itemId: string): Promise<InventoryItemImage[]> {
    return this.makeRequest(() =>
      apiClient<InventoryItemImage[]>(`/inventory/items/${itemId}/images`),
    )
  }

  async reorderItemImages(
    itemId: string,
    imageIds: string[],
  ): Promise<InventoryItemImage[]> {
    return this.makeRequest(() =>
      apiClient<InventoryItemImage[]>(
        `/inventory/items/${itemId}/images/reorder`,
        { method: 'PUT', body: { imageIds } },
      ),
    )
  }

  async deleteItemImage(itemId: string, imageId: string): Promise<void> {
    return this.makeRequest(() =>
      apiClient<void>(`/inventory/items/${itemId}/images/${imageId}`, {
        method: 'DELETE',
      }),
    )
  }

  // =====================
  // Import / Export
  // =====================

  previewImport(
    records: Record<string, unknown>[],
    options: ImportOptions,
  ): Promise<ImportPreviewRecord[]> {
    return this.makeRequest(() =>
      apiClient<ImportPreviewRecord[]>('/inventory/items/import/preview', {
        method: 'POST',
        body: { records, options },
      }),
    )
  }

  importItems(data: {
    records: ImportPreviewRecord[]
    options: ImportOptions
  }): Promise<ImportResult> {
    return this.makeRequest(() =>
      apiClient<ImportResult>('/inventory/items/import', {
        method: 'POST',
        body: data,
      }),
    )
  }

  exportItems(options: ExportOptions): Promise<unknown[]> {
    return this.makeRequest(() =>
      apiClient<unknown[]>('/inventory/items/export', {
        method: 'POST',
        body: options,
      }),
    )
  }

  // =====================
  // Stock Movements
  // =====================

  async getAllocationOptions(
    itemId: string,
    params: InventoryAllocationQuery,
  ): Promise<InventoryAllocationOptions> {
    const query = new URLSearchParams({
      receiving: String(params.receiving),
      page: String(params.page ?? 1),
      search: params.search ?? '',
    })
    if (params.locationId) query.set('locationId', params.locationId)
    return this.makeRequest(() =>
      apiClient<InventoryAllocationOptions>(
        `/inventory/items/${itemId}/allocations?${query}`,
      ),
    )
  }

  async recordMovement(
    data: CreateInventoryMovement | StockCommand<StockMovementPayload>,
  ): Promise<InventoryMovement> {
    return this.makeRequest(() =>
      apiClient<InventoryMovement>('/inventory/movements', {
        method: 'POST',
        body: data,
      }),
    )
  }

  async getItemMovementHistory(
    itemId: string,
    limit = 50,
  ): Promise<InventoryMovement[]> {
    return this.makeRequest(() =>
      apiClient<InventoryMovement[]>(
        `/inventory/movements/items/${itemId}/history?limit=${limit}`,
      ),
    )
  }

  async getMovements(
    params: GetMovementsParams = {},
  ): Promise<InventoryPaginatedResponse<InventoryMovement>> {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value))
      }
    })

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : ''
    return this.makeRequest(() =>
      apiClient<InventoryPaginatedResponse<InventoryMovement>>(
        `/inventory/movements${queryString}`,
      ),
    )
  }

  // =====================
  // Categories
  // =====================

  async getCategories(
    params: GetCategoriesParams = {},
  ): Promise<InventoryCategory[]> {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value))
      }
    })

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : ''
    return this.makeRequest(() =>
      apiClient<InventoryCategory[]>(`/inventory/categories${queryString}`),
    )
  }

  async createCategory(
    data: CreateInventoryCategory,
  ): Promise<InventoryCategory> {
    return this.makeRequest(() =>
      apiClient<InventoryCategory>('/inventory/categories', {
        method: 'POST',
        body: data,
      }),
    )
  }

  async updateCategory(
    id: string,
    data: UpdateInventoryCategory,
  ): Promise<InventoryCategory> {
    return this.makeRequest(() =>
      apiClient<InventoryCategory>(`/inventory/categories/${id}`, {
        method: 'PUT',
        body: data,
      }),
    )
  }

  async deleteCategory(id: string): Promise<void> {
    return this.makeRequest(() =>
      apiClient<void>(`/inventory/categories/${id}`, {
        method: 'DELETE',
      }),
    )
  }

  // =====================
  // Category Types
  // =====================

  async getCategoryTypes(): Promise<InventoryCategoryTypeOption[]> {
    return this.makeRequest(() =>
      apiClient<InventoryCategoryTypeOption[]>('/inventory/category-types'),
    )
  }

  async createCategoryType(
    data: CreateInventoryCategoryTypeOption,
  ): Promise<InventoryCategoryTypeOption> {
    return this.makeRequest(() =>
      apiClient<InventoryCategoryTypeOption>('/inventory/category-types', {
        method: 'POST',
        body: data,
      }),
    )
  }

  async updateCategoryType(
    id: string,
    data: UpdateInventoryCategoryTypeOption,
  ): Promise<InventoryCategoryTypeOption> {
    return this.makeRequest(() =>
      apiClient<InventoryCategoryTypeOption>(
        `/inventory/category-types/${id}`,
        { method: 'PUT', body: data },
      ),
    )
  }

  async deleteCategoryType(id: string): Promise<void> {
    return this.makeRequest(() =>
      apiClient<void>(`/inventory/category-types/${id}`, { method: 'DELETE' }),
    )
  }

  // =====================
  // Movement Reasons
  // =====================

  async getMovementReasons(): Promise<InventoryMovementReasonOption[]> {
    return this.makeRequest(() =>
      apiClient<InventoryMovementReasonOption[]>('/inventory/movement-reasons'),
    )
  }

  async createMovementReason(
    data: CreateInventoryMovementReasonOption,
  ): Promise<InventoryMovementReasonOption> {
    return this.makeRequest(() =>
      apiClient<InventoryMovementReasonOption>('/inventory/movement-reasons', {
        method: 'POST',
        body: data,
      }),
    )
  }

  async updateMovementReason(
    id: string,
    data: UpdateInventoryMovementReasonOption,
  ): Promise<InventoryMovementReasonOption> {
    return this.makeRequest(() =>
      apiClient<InventoryMovementReasonOption>(
        `/inventory/movement-reasons/${id}`,
        { method: 'PUT', body: data },
      ),
    )
  }

  async deleteMovementReason(id: string): Promise<void> {
    return this.makeRequest(() =>
      apiClient<void>(`/inventory/movement-reasons/${id}`, {
        method: 'DELETE',
      }),
    )
  }

  // =====================
  // Locations
  // =====================

  async getLocations(
    params: GetLocationsParams = {},
  ): Promise<InventoryLocation[]> {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value))
      }
    })

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : ''
    return this.makeRequest(() =>
      apiClient<InventoryLocation[]>(`/inventory/locations${queryString}`),
    )
  }

  async createLocation(
    data: CreateInventoryLocation,
  ): Promise<InventoryLocation> {
    return this.makeRequest(() =>
      apiClient<InventoryLocation>('/inventory/locations', {
        method: 'POST',
        body: data,
      }),
    )
  }

  async updateLocation(
    id: string,
    data: UpdateInventoryLocation,
  ): Promise<InventoryLocation> {
    return this.makeRequest(() =>
      apiClient<InventoryLocation>(`/inventory/locations/${id}`, {
        method: 'PUT',
        body: data,
      }),
    )
  }

  async deleteLocation(id: string): Promise<void> {
    return this.makeRequest(() =>
      apiClient<void>(`/inventory/locations/${id}`, {
        method: 'DELETE',
      }),
    )
  }

  // =====================
  // Adjustments
  // =====================

  async getAdjustments(
    params: PaginationParams &
      InventoryAdjustmentFilters = {} as PaginationParams,
  ): Promise<InventoryPaginatedResponse<InventoryAdjustment>> {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value))
      }
    })
    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : ''
    return this.makeRequest(() =>
      apiClient<InventoryPaginatedResponse<InventoryAdjustment>>(
        `/inventory/adjustments${queryString}`,
      ),
    )
  }

  async getAdjustment(id: string): Promise<InventoryAdjustment> {
    return this.makeRequest(() =>
      apiClient<InventoryAdjustment>(`/inventory/adjustments/${id}`),
    )
  }

  async getCountBaseline(
    itemId: string,
    locationId?: string,
  ): Promise<{ quantity: number }> {
    const query = locationId
      ? `?locationId=${encodeURIComponent(locationId)}`
      : ''
    return this.makeRequest(() =>
      apiClient<{ quantity: number }>(
        `/inventory/items/${itemId}/count-baseline${query}`,
      ),
    )
  }

  async createAdjustment(
    data: CreateInventoryAdjustment | StockCommand<StockCountPayload>,
  ): Promise<InventoryAdjustment> {
    return this.makeRequest(() =>
      apiClient<InventoryAdjustment>('/inventory/adjustments', {
        method: 'POST',
        body: data,
      }),
    )
  }

  async approveAdjustment(
    id: string,
    data: ApproveInventoryAdjustment = {},
  ): Promise<InventoryAdjustment> {
    return this.makeRequest(() =>
      apiClient<InventoryAdjustment>(`/inventory/adjustments/${id}/approve`, {
        method: 'POST',
        body: data,
      }),
    )
  }

  async rejectAdjustment(
    id: string,
    data: RejectInventoryAdjustment = {},
  ): Promise<InventoryAdjustment> {
    return this.makeRequest(() =>
      apiClient<InventoryAdjustment>(`/inventory/adjustments/${id}/reject`, {
        method: 'POST',
        body: data,
      }),
    )
  }

  // =====================
  // Analytics
  // =====================

  async getLowStockItems(): Promise<InventoryLowStockItem[]> {
    return this.makeRequest(() =>
      apiClient<InventoryLowStockItem[]>('/inventory/analytics/low-stock'),
    )
  }

  async getValueSummary(): Promise<InventoryValueSummaryResult> {
    return this.makeRequest(() =>
      apiClient<InventoryValueSummaryResult>(
        '/inventory/analytics/value-summary',
      ),
    )
  }

  async getMovementHistory(
    params: GetMovementHistoryParams = {},
  ): Promise<InventoryMovementHistoryResult> {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value))
      }
    })

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : ''
    return this.makeRequest(() =>
      apiClient<InventoryMovementHistoryResult>(
        `/inventory/analytics/movement-history${queryString}`,
      ),
    )
  }

  async getStockHealth(days: number): Promise<InventoryStockHealthResult> {
    return this.makeRequest(() =>
      apiClient<InventoryStockHealthResult>(
        `/inventory/analytics/stock-health?days=${days}`,
      ),
    )
  }

  async getTopMovingItems(
    days = 30,
    limit = 10,
  ): Promise<InventoryTopMovingItem[]> {
    return this.makeRequest(() =>
      apiClient<InventoryTopMovingItem[]>(
        `/inventory/analytics/top-moving-items?days=${days}&limit=${limit}`,
      ),
    )
  }

  async getLocationHierarchy(): Promise<InventoryLocation[]> {
    return this.makeRequest(() =>
      apiClient<InventoryLocation[]>('/inventory/locations/hierarchy'),
    )
  }
}

// Export singleton instance
export const inventoryApi = createSingletonApi(InventoryApi)
